// src/pages/SetPassword.js
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../api";
import "./SetPassword.css";

export default function SetPassword() {
  const { uid, token } = useParams();
  const navigate = useNavigate();

  const [passwords, setPasswords] = useState({
    new_password: "",
    confirm_password: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [message, setMessage] = useState("");
  const [isValidLink, setIsValidLink] = useState(null);

  useEffect(() => {
    console.log("UID:", uid);
    console.log("Token:", token);

    // Skip validation for now - directly allow password setting
    setIsValidLink(true);
    setValidating(false);
  }, [uid, token]);

  const validatePasswords = () => {
    const newErrors = {};

    if (!passwords.new_password) {
      newErrors.new_password = "Password is required";
    } else if (passwords.new_password.length < 8) {
      newErrors.new_password = "Password must be at least 8 characters long";
    }

    if (!passwords.confirm_password) {
      newErrors.confirm_password = "Please confirm your password";
    } else if (passwords.new_password !== passwords.confirm_password) {
      newErrors.confirm_password = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setPasswords({ ...passwords, [name]: value });

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validatePasswords()) {
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      console.log("Sending password reset request...");
      console.log("UID:", uid);
      console.log("Token:", token);

      const response = await api.post("/members/auth/set-password/", {
        uid: uid,
        token: token,
        new_password: passwords.new_password,
      });

      console.log("Password set response:", response.data);
      setMessage("✅ Password set successfully! Redirecting to login...");

      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate("/login");
      }, 2000);

    } catch (error) {
      console.error("Set password error:", error);
      console.error("Error response:", error.response?.data);

      if (error.response?.data) {
        const errorData = error.response.data;

        // Handle field-specific errors
        if (errorData.new_password) {
          setErrors({ new_password: errorData.new_password });
        }

        // Display general error message
        setMessage(errorData.error || "Failed to set password. Please try again.");
      } else if (error.request) {
        // Request was made but no response received
        setMessage("Network error. Please check your connection and try again.");
      } else {
        // Something else happened
        setMessage("An unexpected error occurred. Please try again later.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Show loading state while validating
  if (validating) {
    return (
      <div className="set-password-container">
        <div className="set-password-card">
          <h2>Loading...</h2>
          <p>Please wait...</p>
        </div>
      </div>
    );
  }

  // Show error if link is invalid
  if (isValidLink === false) {
    return (
      <div className="set-password-container">
        <div className="set-password-card">
          <h2>Invalid Link</h2>
          <p className="error-message">{message}</p>
          <p>Password reset links expire after 24 hours for security reasons.</p>
          <button onClick={() => navigate("/login")} className="back-btn">
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  // Main password setting form
  return (
    <div className="set-password-container">
      <div className="set-password-card">
        <h2>Set Your Password</h2>
        <p className="welcome-text">
          Welcome to Marakwet Community in Australia! Please set your password to activate your account.
        </p>

        {message && (
          <div className={message.includes("✅") ? "success-message" : "error-message"}>
            {message}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>New Password</label>
            <input
              type="password"
              name="new_password"
              placeholder="Enter your password (min 8 characters)"
              value={passwords.new_password}
              onChange={handleChange}
              className={errors.new_password ? "error" : ""}
              disabled={loading}
              autoComplete="new-password"
            />
            {errors.new_password && (
              <span className="error-text">{errors.new_password}</span>
            )}
            <small className="password-hint">
              Password must be at least 8 characters long
            </small>
          </div>

          <div className="form-group">
            <label>Confirm Password</label>
            <input
              type="password"
              name="confirm_password"
              placeholder="Confirm your password"
              value={passwords.confirm_password}
              onChange={handleChange}
              className={errors.confirm_password ? "error" : ""}
              disabled={loading}
              autoComplete="new-password"
            />
            {errors.confirm_password && (
              <span className="error-text">{errors.confirm_password}</span>
            )}
          </div>

          <button type="submit" disabled={loading} className="submit-btn">
            {loading ? "Setting Password..." : "Set Password & Activate Account"}
          </button>
        </form>

        <div className="login-link">
          Already have a password? <a href="/login">Login here</a>
        </div>
      </div>
    </div>
  );
}